// apps/client/src/components/reports/RctiMatrix.tsx
// RCTI Cashflow Matrix - 3×4 grid showing Run Number × Payment Type breakdown

import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import { RctiRunBreakdown } from '../../types';

interface RctiMatrixProps {
  runs: RctiRunBreakdown[];
  monthTotals: {
    upfrontCents: number;
    trailCents: number;
    otherCents: number;
    aggregatorChargeCents: number;
    totalCents: number;
  };
}

const RctiMatrix: React.FC<RctiMatrixProps> = ({ runs, monthTotals }) => {
  // Create a map for quick lookup by run number
  const runMap = new Map<number | null, RctiRunBreakdown>();
  runs.forEach((run) => runMap.set(run.runNumber, run));

  // Get data for specific run number (default to zeros if not found)
  const getRun = (runNumber: 1 | 2 | 3): RctiRunBreakdown => {
    return (
      runMap.get(runNumber) || {
        runNumber,
        upfrontCents: 0,
        trailCents: 0,
        otherCents: 0,
        aggregatorChargeCents: 0,
        totalCents: 0,
        loanCount: 0,
      }
    );
  };

  const run1 = getRun(1);
  const run2 = getRun(2);
  const run3 = getRun(3);

  // Calculate column totals (sum across all runs for each payment type)
  const columnTotals = {
    run1Total: run1.totalCents,
    run2Total: run2.totalCents,
    run3Total: run3.totalCents,
  };

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-900">
          <tr>
            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">
              Run Number
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
              Upfront
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
              Trail
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">
              Deductions
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold text-amber-400">
              Aggregator Charges
            </th>
            <th className="px-6 py-4 text-right text-sm font-semibold text-blue-400">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {/* Run 1 */}
          <tr className="hover:bg-gray-750 transition-colors">
            <td className="px-6 py-4 text-white font-medium">Run 1</td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run1.upfrontCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run1.trailCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run1.otherCents)}
            </td>
            <td className="px-6 py-4 text-right text-amber-400">
              {formatCurrency(run1.aggregatorChargeCents ?? 0)}
            </td>
            <td className="px-6 py-4 text-right text-blue-400 font-semibold">
              {formatCurrency(columnTotals.run1Total)}
            </td>
          </tr>

          {/* Run 2 */}
          <tr className="hover:bg-gray-750 transition-colors">
            <td className="px-6 py-4 text-white font-medium">Run 2</td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run2.upfrontCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run2.trailCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run2.otherCents)}
            </td>
            <td className="px-6 py-4 text-right text-amber-400">
              {formatCurrency(run2.aggregatorChargeCents ?? 0)}
            </td>
            <td className="px-6 py-4 text-right text-blue-400 font-semibold">
              {formatCurrency(columnTotals.run2Total)}
            </td>
          </tr>

          {/* Run 3 */}
          <tr className="hover:bg-gray-750 transition-colors">
            <td className="px-6 py-4 text-white font-medium">Run 3</td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run3.upfrontCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run3.trailCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(run3.otherCents)}
            </td>
            <td className="px-6 py-4 text-right text-amber-400">
              {formatCurrency(run3.aggregatorChargeCents ?? 0)}
            </td>
            <td className="px-6 py-4 text-right text-blue-400 font-semibold">
              {formatCurrency(columnTotals.run3Total)}
            </td>
          </tr>

          {/* TOTAL Row */}
          <tr className="bg-gray-900 font-bold">
            <td className="px-6 py-4 text-white">TOTAL</td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(monthTotals.upfrontCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(monthTotals.trailCents)}
            </td>
            <td className="px-6 py-4 text-right text-white">
              {formatCurrency(monthTotals.otherCents)}
            </td>
            <td className="px-6 py-4 text-right text-amber-400">
              {formatCurrency(monthTotals.aggregatorChargeCents ?? 0)}
            </td>
            <td className="px-6 py-4 text-right text-blue-400 text-lg">
              {formatCurrency(monthTotals.totalCents)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default RctiMatrix;
